// SettingsModal — the account's settings, on Soft (round 5, 2026-10-13). It
// opens from the account menu in the shared centred FilterDialog, as the
// Trenches Customize, Discover's Table settings and the token page's settings
// do, and is built the way they are: a head with a mark, the name, Reset and
// Close; the categories on a Soft track across the dialog, each with its mark;
// under the track the category's settings in captioned groups, each group a
// plate of lines — a line is its mark, its name, what it does, and its
// control. The dialog is one size whatever category is on.
//
// A setting wears the control its choice is: on or off is a switch, a whole
// number a stepper, a quantity a slider with its figure on a plate beside it,
// a place a small picture of where a toast lands.
//
// HONESTY. There is no preferences backend: a choice is saved in the browser
// (settingsStore.ts, `wt-set:*`) and survives a reload; whatever else the
// storage holds reads as the default. Every control that is drawn live is read
// by something:
//   Display notifications, Toast duration, Mute while inactive — the wallet
//     alerts (notifications/WalletTrackerProvider);
//   Toast position, Max toasts shown — the toast stack (ui/pixel-toast);
//   Count network gas in PnL — the portfolio's requests (api/portfolio).
// What nothing reads yet is drawn as not available, where it stands, with its
// saved value: the sounds (the terminal has none) and the order-fill and
// migration alerts (nothing sends them). Display and Privacy have nothing to
// set and say so. Nothing here changes what a Buy or a Sell does.
//
// The tracked wallets are the account's, on the server: the form POSTs, a
// rule's switch and its Save PATCH, its bin DELETEs — handlers, validation and
// requests are as they were; Reset never touches them.

import {
  AppWindowIcon,
  ArrowCounterClockwiseIcon,
  ArrowsLeftRightIcon,
  BellIcon,
  BellRingingIcon,
  CaretDownIcon,
  CheckIcon,
  GasPumpIcon,
  GearSixIcon,
  type Icon,
  ListChecksIcon,
  LockSimpleIcon,
  MagnifyingGlassIcon,
  MonitorIcon,
  MoonIcon,
  PlusIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  StackIcon,
  TimerIcon,
  TrashIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react'
import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import type { TrackedWalletRule } from '../../api/userland'
import type { UseWalletTracker } from '../../hooks/useWalletTracker'
import { useAuthMethods } from '../../lib/authMethods'
import { linkedLoginMethods } from '../../lib/profileIdentity'
import { SOUND_EVENTS, SOUND_EVENT_LABEL } from '../../lib/sound/settings'
import soft from '../../styles/soft.module.css'
import { useOptionalAccountSession } from '../../wallet/AccountSessionContext'
import { useWalletTrackerOptional } from '../notifications/WalletTrackerProvider'
import { SoundSettingsPanel } from '../sound/SoundSettingsPanel'
import { Described } from '../ui/Described'
import { FilterDialog } from '../ui/FilterDialog'
import { SoftSlider, SoftStepper, SoftSwitch, SoftValue } from '../ui/SoftControls'
import { figs } from '../ui/fig'
import { useOptionalPixelToast } from '../ui/pixel-toast'
import { ChangePassword } from './ChangePassword'
import { ConfirmEmail } from './ConfirmEmail'
import styles from './settings.module.css'
import {
  DISPLAY_NOTIFICATIONS,
  MAX_TOASTS,
  MUTE_WHILE_INACTIVE,
  TOAST_DURATION,
  TOAST_POSITION,
  TOAST_POSITIONS,
  TOAST_POSITION_DEFAULT,
  type ToastPosition,
  clearSettings,
  readChoice,
  readInt,
  readSetting,
  settingsVersion,
  subscribeSettings,
  toastPosition,
  writeSetting,
} from './settingsStore'

// the readers the rest of the app has always taken from this module
export { readSetting, writeSetting } from './settingsStore'

interface Props {
  open: boolean
  onClose: () => void
}

interface ItemBase {
  /** The name the value is saved under (`wt-set:<name>`). It is what the
   *  setting was first called: a reworded label must never move a saved value. */
  name: string
  label: string
  hint: string
  Icon: Icon
}

type Item =
  | (ItemBase & { kind: 'switch'; initial: boolean })
  | (ItemBase & { kind: 'position' })
  | (ItemBase & { kind: 'stepper'; initial: number; min: number; max: number })
  | (ItemBase & {
      kind: 'slider'
      initial: number
      min: number
      max: number
      unit: string
      /** The unit as a reader says it ("seconds"). */
      unitWord: string
    })
  | (ItemBase & { kind: 'select'; options: readonly string[]; initial: string })

interface Group {
  label: string
  hint?: string
  /** Why none of the group's settings is available yet. They are drawn where
   *  they stand, with what is saved, and cannot be changed. */
  soon?: string
  items: readonly Item[]
  /** A group drawn by a real settings panel of its own instead of lines; a
   *  search finds it by these words. */
  panel?: { Panel: () => ReactNode; words: readonly string[] }
}

// Trading: gas is a real cost of a trade but not one the product takes, so by
// default it is OUTSIDE PnL, not quietly inside. The portfolio's answer says
// which number came back (pnl_includes_network_fees), so one cannot be shown
// while the other is meant.
export const COUNT_GAS_IN_PNL = 'Count network gas in PnL'

/** True when the account asked for gas inside PnL. Default is OFF. */
export function countsGasInPnL(): boolean {
  return readSetting(COUNT_GAS_IN_PNL, false)
}

const NOTIFICATION_GROUPS: readonly Group[] = [
  {
    label: 'Wallet alerts',
    hint: 'What a tracked wallet does, as it happens',
    items: [
      {
        kind: 'switch',
        name: DISPLAY_NOTIFICATIONS,
        label: 'Display notifications',
        hint: 'Display wallet tracker toasts and notification cards',
        Icon: BellRingingIcon,
        initial: true,
      },
      {
        kind: 'slider',
        ...TOAST_DURATION,
        label: 'Toast duration',
        hint: 'How long a wallet alert stays',
        Icon: TimerIcon,
        unit: 's',
        unitWord: 'seconds',
      },
      {
        kind: 'switch',
        name: MUTE_WHILE_INACTIVE,
        label: 'Mute while inactive',
        hint: "Pause toasts when the terminal isn't focused",
        Icon: MoonIcon,
        initial: false,
      },
    ],
  },
  {
    label: 'Toasts',
    hint: 'Every toast of the terminal',
    items: [
      {
        kind: 'position',
        name: TOAST_POSITION,
        label: 'Toast position',
        hint: 'Where a toast lands',
        Icon: AppWindowIcon,
      },
      {
        kind: 'stepper',
        ...MAX_TOASTS,
        label: 'Max toasts shown',
        // ui/pixel-toast fitStack: the oldest goes, a trade under way never
        hint: 'One more pushes the oldest out; a trade under way stays',
        Icon: StackIcon,
      },
    ],
  },
  {
    // The terminal's sounds (lib/sound): the same panel as the Sound button in
    // Trenches, one set of settings. The old lines here (Transaction sounds,
    // Volume, Sound pack) were never read, and the group said "The terminal
    // has no sounds yet" after the sounds had shipped.
    label: 'Sounds',
    hint: 'Your results and alerts, as the Sound button in Trenches sets them',
    items: [],
    panel: {
      Panel: SoundSettingsPanel,
      words: [
        'sound',
        'sounds',
        'volume',
        'mute',
        ...SOUND_EVENTS.map((e) => SOUND_EVENT_LABEL[e]),
      ],
    },
  },
  {
    label: 'More alerts',
    soon: 'Nothing sends these yet',
    items: [
      {
        kind: 'switch',
        name: 'Order fill alerts',
        label: 'Order fill alerts',
        hint: 'Notify when limit and DCA orders execute',
        Icon: ListChecksIcon,
        initial: true,
      },
      {
        kind: 'switch',
        name: 'Token migration alerts',
        label: 'Token migration alerts',
        hint: 'When a bonding token migrates to a DEX',
        Icon: RocketLaunchIcon,
        initial: true,
      },
    ],
  },
]

const TRADING_GROUPS: readonly Group[] = [
  {
    label: 'Profit and loss',
    hint: 'How the portfolio counts it',
    items: [
      {
        kind: 'switch',
        name: COUNT_GAS_IN_PNL,
        label: COUNT_GAS_IN_PNL,
        hint: 'Subtract the network fee each trade burned from realized PnL',
        Icon: GasPumpIcon,
        initial: false,
      },
    ],
  },
]

interface Category {
  key: 'notifications' | 'trading' | 'security' | 'display' | 'privacy'
  label: string
  Icon: Icon
  groups: readonly Group[]
  /** Said under the category's settings: what it does not decide. */
  note?: string
}
type CategoryKey = Category['key']

// the category the dialog opens on, and the one a saved choice falls back to
const NOTIFICATIONS: Category = {
  key: 'notifications',
  label: 'Notifications',
  Icon: BellIcon,
  groups: NOTIFICATION_GROUPS,
}

const CATEGORIES: readonly Category[] = [
  NOTIFICATIONS,
  {
    key: 'trading',
    label: 'Trading',
    Icon: ArrowsLeftRightIcon,
    groups: TRADING_GROUPS,
    note: 'Slippage, presets and quick amounts are set in the trade form itself. Nothing here changes what a Buy or a Sell does.',
  },
  // the account's sign-in: the password of an email account (ChangePassword)
  { key: 'security', label: 'Security', Icon: ShieldCheckIcon, groups: [] },
  // nothing to set in these two yet: each says so
  { key: 'display', label: 'Display', Icon: MonitorIcon, groups: [] },
  { key: 'privacy', label: 'Privacy', Icon: LockSimpleIcon, groups: [] },
]

const ALL_ITEMS: readonly Item[] = CATEGORIES.flatMap((c) => c.groups.flatMap((g) => g.items))

// what is saved for a setting, or its default when nothing usable is
function savedIsDefault(it: Item): boolean {
  switch (it.kind) {
    case 'switch':
      return readSetting(it.name, it.initial) === it.initial
    case 'position':
      return toastPosition() === TOAST_POSITION_DEFAULT
    case 'select':
      return readChoice(it.name, it.options, it.initial) === it.initial
    default:
      return readInt(it.name, it.initial, it.min, it.max) === it.initial
  }
}

const itemMatches = (it: Item, q: string) => `${it.label} ${it.hint}`.toLowerCase().includes(q)

// The dialog (a native showModal) gives the focus to the first control it
// finds unless one asks for it. Reset applies at once, so it must not be the
// one: Close asks. (React's `autoFocus` focuses on mount, before the dialog is
// open, and writes no attribute for the dialog to find.)
const takesOpeningFocus = (el: HTMLButtonElement | null) => el?.setAttribute('autofocus', '')

export function SettingsModal({ open, onClose }: Props) {
  // the category and the search outlive a close, as they always have
  const [category, setCategory] = useState<CategoryKey>('notifications')
  const [query, setQuery] = useState('')
  if (!open) return null
  return (
    <FilterDialog label="Settings" onClose={onClose}>
      <SettingsPanel
        category={category}
        onCategory={setCategory}
        query={query}
        onQuery={setQuery}
        onClose={onClose}
      />
    </FilterDialog>
  )
}

function SettingsPanel({
  category,
  onCategory,
  query,
  onQuery,
  onClose,
}: {
  category: CategoryKey
  onCategory: (next: CategoryKey) => void
  query: string
  onQuery: (next: string) => void
  onClose: () => void
}) {
  // Reset is lit only while something differs from the defaults
  useSyncExternalStore(subscribeSettings, settingsVersion)
  const atDefault = ALL_ITEMS.every(savedIsDefault)
  // a reset remounts the lines: each reads its default again
  const [epoch, setEpoch] = useState(0)

  const shown = CATEGORIES.find((c) => c.key === category) ?? NOTIFICATIONS
  const q = query.trim().toLowerCase()

  return (
    <div className={styles.panel}>
      <header className={styles.head}>
        <span className={styles.titleIcon} aria-hidden="true">
          <GearSixIcon size={18} weight="bold" />
        </span>
        <h2 className={styles.title}>Settings</h2>
        <div className={styles.actions}>
          {/* With nothing to reset it is marked off, not `disabled`: pressed
              from the keyboard it turns itself off under the focus, and a
              disabled button would drop that focus out of the dialog. */}
          <Described
            as="button"
            type="button"
            className={`${soft.btn} ${soft.round} ${soft.small} ${styles.reset}`}
            onClick={() => {
              if (atDefault) return
              clearSettings(ALL_ITEMS.map((it) => it.name))
              setEpoch((n) => n + 1)
            }}
            aria-disabled={atDefault || undefined}
            aria-label="Reset to defaults"
            description="Reset to defaults — tracked wallets are kept"
          >
            <ArrowCounterClockwiseIcon size={16} weight="bold" />
          </Described>
          <button
            ref={takesOpeningFocus}
            type="button"
            className={`${soft.btn} ${soft.round} ${soft.small}`}
            onClick={onClose}
            aria-label="Close settings"
          >
            <XIcon size={16} weight="bold" />
          </button>
        </div>
      </header>

      <div className={styles.searchRow}>
        <SearchField query={query} onQuery={onQuery} />
      </div>

      {/* the head's last row: the track of categories, sharing it evenly.
          While a search is on none is pressed — every one of them is searched —
          and picking one ends the search there. */}
      <nav className={`${soft.track} ${styles.cats}`} aria-label="Settings sections">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`${soft.seg} ${styles.cat}`}
            aria-pressed={q === '' && c.key === shown.key}
            onClick={() => {
              onCategory(c.key)
              onQuery('')
            }}
          >
            <c.Icon size={16} weight="bold" aria-hidden="true" />
            {figs(c.label)}
          </button>
        ))}
      </nav>

      {/* keyed by what it shows, so what comes in is drawn in, not morphed */}
      <SettingsBody
        key={`${epoch}:${q === '' ? shown.key : 'search'}`}
        categories={CATEGORIES}
        shown={shown}
        query={query}
      />
    </div>
  )
}

function SearchField({ query, onQuery }: { query: string; onQuery: (next: string) => void }) {
  return (
    <label className={`${soft.field} ${styles.search}`}>
      <MagnifyingGlassIcon size={16} weight="bold" aria-hidden="true" />
      <input
        placeholder="Search settings"
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        aria-label="Search settings"
      />
      {query !== '' && (
        <button
          type="button"
          className={styles.clear}
          onClick={() => onQuery('')}
          aria-label="Clear search"
        >
          <XIcon size={12} weight="bold" aria-hidden="true" />
        </button>
      )}
    </label>
  )
}

// the tracked wallets answer a search by what they are and by their own rules
const TRACKER_WORDS = 'tracked wallets wallet tracking tracked address minimum amount'
const trackerMatches = (tracker: UseWalletTracker, q: string) =>
  TRACKER_WORDS.includes(q) ||
  tracker.rules.some((rule) => `${rule.label} ${rule.address}`.toLowerCase().includes(q))

// The tracked wallets stand first in Notifications; a search finds them from
// any category. Null where they do not show (or the tracker is switched off).
// The password form is drawn for an email account, when the API serves the
// change (email codes on): a Telegram, Google or TON Connect account has none.
function usePasswordSetting(): boolean {
  const session = useOptionalAccountSession()
  const auth = useAuthMethods()
  const me = session?.me
  if (!me || !auth?.emailCodes) return false
  return linkedLoginMethods(me).some((method) => method.kind === 'email')
}

function trackerIn(tracker: UseWalletTracker | null, shown: Category, q: string) {
  if (!tracker) return null
  const here = q === '' ? shown.key === 'notifications' : trackerMatches(tracker, q)
  return here ? tracker : null
}

interface Section {
  cat: Category
  group: Group
  items: readonly Item[]
}

// a category's groups with the settings that answer `q` (all of them for '')
const sectionsOf = (cat: Category, q: string): Section[] =>
  cat.groups
    .map((group) => ({
      cat,
      group,
      items: q === '' ? group.items : group.items.filter((it) => itemMatches(it, q)),
    }))
    .filter(
      (section) =>
        section.items.length > 0 ||
        (section.group.panel != null &&
          (q === '' || section.group.panel.words.some((w) => w.toLowerCase().includes(q))))
    )

// What stands under the track: the category that is on, all of it — or, with a
// search, whatever answers it in every category.
function SettingsBody({
  categories,
  shown,
  query,
}: {
  categories: readonly Category[]
  shown: Category
  query: string
}) {
  const tracker = useWalletTrackerOptional()
  const passwordHere = usePasswordSetting()
  const q = query.trim().toLowerCase()
  const searching = q !== ''
  const sections = searching
    ? categories.flatMap((cat) => sectionsOf(cat, q))
    : sectionsOf(shown, '')
  const trackerShown = trackerIn(tracker, shown, q)
  const passwordShown =
    passwordHere &&
    (searching
      ? (q.length >= 3 && 'password'.includes(q)) || q.includes('password')
      : shown.key === 'security')
  const emailShown =
    passwordHere &&
    (searching
      ? (q.length >= 3 && 'email'.includes(q)) || q.includes('email')
      : shown.key === 'security')
  const nothing = sections.length === 0 && !trackerShown && !passwordShown && !emailShown
  return (
    <div className={styles.body} data-category={searching ? 'search' : shown.key}>
      {nothing && <Nothing shown={shown} query={searching ? query : null} />}
      {trackerShown && <TrackedWalletSettings tracker={trackerShown} />}
      {emailShown && <ConfirmEmail />}
      {passwordShown && <ChangePassword />}
      {sections.map((section) => (
        <GroupSection
          key={`${section.cat.key}:${section.group.label}`}
          section={section}
          searching={searching}
        />
      ))}
      {!searching && shown.note && <p className={styles.note}>{shown.note}</p>}
    </div>
  )
}

// A group under its caption: a plate of its settings' lines. One that is not
// available yet says so beside its name, and why; in a search's results a
// group says which category it is from.
function GroupSection({ section, searching }: { section: Section; searching: boolean }) {
  const { cat, group, items } = section
  const about = group.soon ?? (searching ? `In ${cat.label}` : group.hint)
  return (
    <section className={styles.group} aria-label={group.label}>
      <div className={styles.groupHead}>
        <h3 className={styles.caption}>{group.label}</h3>
        {group.soon && <span className={styles.soon}>{figs('Soon')}</span>}
        {about && <span className={styles.hint}>{about}</span>}
      </div>
      {group.panel ? (
        <div className={styles.lines}>
          <group.panel.Panel />
        </div>
      ) : (
        <div className={styles.lines}>
          {items.map((it) => (
            <SettingLine key={it.name} item={it} off={group.soon} />
          ))}
        </div>
      )}
    </section>
  )
}

// A category with nothing to set, or a search with nothing found: said plainly.
function Nothing({ shown, query }: { shown: Category; query: string | null }) {
  const Mark = query == null ? shown.Icon : MagnifyingGlassIcon
  return (
    <div className={styles.blank}>
      <span className={styles.blankMark} aria-hidden="true">
        <Mark size={22} weight="bold" />
      </span>
      <span className={styles.blankTitle}>
        {query == null ? 'Nothing to set here yet' : `No settings match “${query}”.`}
      </span>
      <span className={styles.blankSub}>
        {query == null
          ? `${shown.label} settings are not available in this version.`
          : 'Every category was searched.'}
      </span>
    </div>
  )
}

// One setting on its line. `off` is why it is not available: its control is
// drawn where it stands, with what is saved, and takes no input.
function SettingLine({ item, off }: { item: Item; off?: string }) {
  switch (item.kind) {
    case 'switch':
      return <SwitchLine item={item} off={off} />
    case 'position':
      return <PositionLine item={item} off={off} />
    case 'stepper':
      return <StepperLine item={item} off={off} />
    case 'slider':
      return <SliderLine item={item} off={off} />
    default:
      return (
        <Line item={item} off={off}>
          <SettingsSelect
            name={item.name}
            label={item.label}
            options={item.options}
            initial={item.initial}
            disabled={off != null}
          />
        </Line>
      )
  }
}

// A line of a plate: the setting's mark, its name over what it does, and its
// control at the right.
function Line({ item, off, children }: { item: Item; off?: string; children: ReactNode }) {
  return (
    <Described
      as="div"
      className={styles.line}
      data-off={off != null || undefined}
      description={off}
    >
      <span className={styles.lineIcon} aria-hidden="true">
        <item.Icon size={16} weight="bold" />
      </span>
      <span className={styles.lineText}>
        <span className={styles.lineLabel}>{item.label}</span>
        <span className={styles.lineHint}>{item.hint}</span>
      </span>
      <span className={styles.lineCtl}>{children}</span>
    </Described>
  )
}

function SwitchLine({ item, off }: { item: Item & { kind: 'switch' }; off?: string }) {
  const [on, setOn] = useState(() => readSetting(item.name, item.initial))
  return (
    <Line item={item} off={off}>
      <SoftSwitch
        checked={on}
        label={item.label}
        disabled={off != null}
        onChange={(next) => {
          setOn(next)
          writeSetting(item.name, next)
        }}
      />
    </Line>
  )
}

// Clamped numeric input state shared by the stepper and the sliders: keeps a
// live text value (digits only) and a committed number in [min, max]. Typing a
// too-big value snaps to max, 0/empty to min, non-digits are rejected.
// Committed values persist per setting.
function useNumberInput(name: string, initial: number, min: number, max: number) {
  const start = readInt(name, initial, min, max)
  const [value, setValue] = useState(start)
  const [text, setText] = useState(String(start))
  const commit = (raw: string) => {
    const digits = raw.replace(/\D/g, '')
    if (digits === '') {
      setText('')
      return
    }
    const n = Math.max(min, Math.min(max, Number.parseInt(digits, 10)))
    setText(String(n))
    setValue(n)
    writeSetting(name, n)
  }
  const blur = () => {
    if (text === '') {
      setText(String(min))
      setValue(min)
      writeSetting(name, min)
    }
  }
  const set = (n: number) => {
    const c = Math.max(min, Math.min(max, n))
    setValue(c)
    setText(String(c))
    writeSetting(name, c)
  }
  return { value, text, commit, blur, set }
}

function StepperLine({ item, off }: { item: Item & { kind: 'stepper' }; off?: string }) {
  const num = useNumberInput(item.name, item.initial, item.min, item.max)
  return (
    <Line item={item} off={off}>
      <SoftStepper
        value={num.value}
        text={num.text}
        min={item.min}
        max={item.max}
        label={item.label}
        onText={num.commit}
        onBlur={num.blur}
        onStep={num.set}
        disabled={off != null}
      />
    </Line>
  )
}

function SliderLine({ item, off }: { item: Item & { kind: 'slider' }; off?: string }) {
  const num = useNumberInput(item.name, item.initial, item.min, item.max)
  return (
    <Line item={item} off={off}>
      <SoftSlider
        className={styles.lineSlider}
        value={num.value}
        min={item.min}
        max={item.max}
        label={item.label}
        valueText={`${num.value} ${item.unitWord}`}
        onChange={num.set}
        disabled={off != null}
      />
      <SoftValue
        text={num.text}
        unit={item.unit}
        label={`${item.label}, ${item.unitWord}`}
        onText={num.commit}
        onBlur={num.blur}
        disabled={off != null}
      />
    </Line>
  )
}

/** One of a few named options, on a menu. Exported for its test: the one
 *  setting that wears it (Sound pack) is not available until the terminal has
 *  sounds, so nothing in the modal opens it yet. */
export function SettingsSelect({
  name,
  label,
  options,
  initial,
  disabled = false,
}: {
  name: string
  label: string
  options: readonly string[]
  initial: string
  disabled?: boolean
}) {
  const [val, setVal] = useState(() => readChoice(name, options, initial))
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])
  return (
    <div className={styles.dropdown} ref={ref}>
      <button
        type="button"
        className={`${soft.btn} ${styles.select}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${label}: ${val}`}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        {figs(val)}
        <CaretDownIcon size={12} weight="bold" aria-hidden="true" />
      </button>
      {open && (
        <div className={`${soft.menu} ${styles.menu}`}>
          {options.map((o) => (
            <button
              key={o}
              type="button"
              className={soft.item}
              aria-pressed={o === val}
              onClick={() => {
                setVal(o)
                writeSetting(name, o)
                setOpen(false)
              }}
            >
              <span className={styles.menuLabel}>{o}</span>
              {o === val && <CheckIcon size={14} weight="bold" aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Where a toast lands: six small pictures of the window, each with a toast in
// its place — the raised capsule the real toasts are. Picking one moves the
// stack at once and sends a toast there to show it.
function PositionLine({ item, off }: { item: Item; off?: string }) {
  const toast = useOptionalPixelToast()
  const [sel, setSel] = useState<ToastPosition>(toastPosition)
  return (
    <Described
      as="div"
      className={styles.line}
      data-stacked=""
      data-off={off != null || undefined}
      description={off}
    >
      <div className={styles.lineHead}>
        <span className={styles.lineIcon} aria-hidden="true">
          <item.Icon size={16} weight="bold" />
        </span>
        <span className={styles.lineText}>
          <span className={styles.lineLabel}>{item.label}</span>
          <span className={styles.lineHint}>{item.hint}</span>
        </span>
        <span className={styles.lineValue}>{sel}</span>
      </div>
      <div className={styles.posGrid}>
        {TOAST_POSITIONS.map((p) => {
          const [v, h] = p.toLowerCase().split(' ')
          return (
            <button
              key={p}
              type="button"
              className={styles.posCell}
              aria-pressed={p === sel}
              disabled={off != null}
              onClick={() => {
                setSel(p)
                writeSetting(TOAST_POSITION, p)
                toast?.push({ tone: 'ok', content: 'Toasts land here', duration: 1600 })
              }}
            >
              <span className={styles.posStage} data-v={v} data-h={h} aria-hidden="true">
                <span className={styles.miniToast}>
                  <i className={styles.miniMark} />
                  <i className={styles.miniBar} />
                </span>
              </span>
              <span className={styles.posLabel}>{p}</span>
            </button>
          )
        })}
      </div>
    </Described>
  )
}

function TrackedWalletSettings({ tracker }: { tracker: UseWalletTracker }) {
  const [label, setLabel] = useState('')
  const [address, setAddress] = useState('')
  const [minimum, setMinimum] = useState('0')
  const [busy, setBusy] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const atLimit = Boolean(tracker.limit && tracker.limit.count >= tracker.limit.max)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const minValue = Number(minimum)
    if (!label.trim() || !address.trim()) {
      setLocalError('Label and TON address are required.')
      return
    }
    if (!Number.isFinite(minValue) || minValue < 0) {
      setLocalError('Minimum USD value must be zero or greater.')
      return
    }
    setBusy(true)
    setLocalError(null)
    try {
      await tracker.createRule({
        label: label.trim(),
        address: address.trim(),
        min_value_usd: minValue,
      })
      setLabel('')
      setAddress('')
      setMinimum('0')
    } catch {
      // The hook exposes the backend's scoped error below.
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className={styles.group} aria-label="Tracked wallets">
      <div className={styles.groupHead}>
        <h3 className={styles.caption}>Tracked wallets</h3>
        <span className={styles.hint}>Realtime buy, sell and transfer alerts</span>
        <Described
          as="span"
          className={styles.count}
          description="Tracked wallets, of the most an account may have"
        >
          {tracker.limit ? `${tracker.limit.count} / ${tracker.limit.max}` : '— / 200'}
        </Described>
      </div>

      <form className={styles.trackerAdd} onSubmit={(event) => void submit(event)}>
        <label className={`${soft.field} ${styles.tField}`}>
          <input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Label"
            aria-label="Tracked wallet label"
            maxLength={64}
            disabled={busy || atLimit}
          />
        </label>
        <label className={`${soft.field} ${styles.tField}`}>
          <input
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            placeholder="TON address"
            aria-label="Tracked wallet address"
            disabled={busy || atLimit}
          />
        </label>
        <label className={`${soft.field} ${styles.tField} ${styles.tMin}`}>
          <span>$ min</span>
          <input
            value={minimum}
            onChange={(event) => setMinimum(event.target.value)}
            inputMode="decimal"
            aria-label="Tracked wallet minimum USD"
            disabled={busy || atLimit}
          />
        </label>
        <button
          type="submit"
          className={`${soft.action} ${styles.tAdd}`}
          disabled={busy || atLimit}
          aria-label="Add tracked wallet"
        >
          <PlusIcon size={14} weight="bold" aria-hidden="true" />
          {figs(busy ? 'Adding…' : 'Add')}
        </button>
      </form>

      {(localError || tracker.actionError) && (
        <div className={styles.tError} role="alert">
          <WarningCircleIcon size={14} weight="bold" aria-hidden="true" />
          {figs(localError || tracker.actionError)}
        </div>
      )}
      {atLimit && (
        <div className={styles.tNote}>200-wallet limit reached, including disabled rules.</div>
      )}
      {tracker.status === 'loading' ? (
        <div className={`${styles.lines} ${styles.tBlank}`}>Loading tracked wallets…</div>
      ) : tracker.rules.length === 0 ? (
        <div className={`${styles.lines} ${styles.tBlank}`}>No tracked wallets yet.</div>
      ) : (
        <div className={styles.lines}>
          {tracker.rules.map((rule) => (
            <TrackedWalletRuleRow key={rule.id} rule={rule} tracker={tracker} />
          ))}
        </div>
      )}
    </section>
  )
}

function TrackedWalletRuleRow({
  rule,
  tracker,
}: {
  rule: TrackedWalletRule
  tracker: UseWalletTracker
}) {
  const [label, setLabel] = useState(rule.label)
  const [minimum, setMinimum] = useState(String(rule.min_value_usd))
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    setLabel(rule.label)
    setMinimum(String(rule.min_value_usd))
  }, [rule.label, rule.min_value_usd])

  const save = async () => {
    const minValue = Number(minimum)
    if (!label.trim() || !Number.isFinite(minValue) || minValue < 0) return
    setBusy(true)
    try {
      await tracker.updateRule(rule.id, { label: label.trim(), min_value_usd: minValue })
    } finally {
      setBusy(false)
    }
  }
  // presentation only: the check is lit while the line differs from the rule
  const dirty = label.trim() !== rule.label || Number(minimum) !== rule.min_value_usd

  return (
    <div className={styles.rule} data-enabled={rule.enabled || undefined}>
      <SoftSwitch
        checked={rule.enabled}
        label={`Track ${rule.label}`}
        disabled={busy}
        onChange={() =>
          void tracker.updateRule(rule.id, { enabled: !rule.enabled }).catch(() => {})
        }
      />
      <div className={styles.ruleMain}>
        <input
          className={styles.ruleLabel}
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          maxLength={64}
          aria-label={`Label for ${rule.label}`}
        />
        <span className={styles.ruleAddress}>{rule.address}</span>
      </div>
      <label className={styles.ruleMin}>
        <span>$</span>
        <input
          value={minimum}
          onChange={(event) => setMinimum(event.target.value)}
          inputMode="decimal"
          aria-label={`Minimum USD for ${rule.label}`}
        />
      </label>
      <Described
        as="button"
        type="button"
        className={`${soft.btn} ${soft.round} ${soft.small} ${styles.ruleSave}`}
        data-dirty={dirty || undefined}
        onClick={() => void save().catch(() => {})}
        disabled={busy}
        aria-label={`Save ${rule.label}`}
        description="Save the label and the minimum"
      >
        <CheckIcon size={15} weight="bold" />
      </Described>
      <Described
        as="button"
        type="button"
        className={`${soft.btn} ${soft.round} ${soft.small} ${styles.ruleDelete}`}
        onClick={() => void tracker.deleteRule(rule.id).catch(() => {})}
        disabled={busy}
        aria-label={`Delete ${rule.label}`}
        description="Stop tracking this wallet"
      >
        <TrashIcon size={15} weight="bold" />
      </Described>
    </div>
  )
}
